import { ApiError } from '@lib/api';
import {
    Button,
    Modal,
    PropagationNotice,
    SelectField,
    TextAreaField,
    TextField,
    WriteError,
} from '@shared/ui';
import { useState } from 'react';

import { useCreateFlag } from '../hooks/use-flags';
import { explainFlagKeyRejection, rejectFlagKey } from '../schemas/flag-key';

import type { FlagLifecycle } from '../schemas/flag';

/**
 * Create a flag at runtime — the action that has no equivalent in
 * VeevartBackend, where a flag exists because someone merged a PR.
 *
 * ## The confirm step is the point of this dialog
 *
 * Losing the code-owned catalog lost one safety property that nothing else
 * replaces: **a typo'd key is not an error.** Upstream, `checkout.new-chekout`
 * fails to compile. Here it creates a second flag, sitting in the catalog
 * looking exactly as legitimate as the one that was meant, gating nothing, and
 * un-renameable — keys are never reused, because defaults, overrides and audit
 * rows are all filed under them.
 *
 * So the flow is deliberately two steps. The second screen exists only to put
 * the key in front of the operator, on its own, after they have stopped typing,
 * and make them agree to it. It is the last human check before a permanent row.
 */

const LIFECYCLE_OPTIONS: readonly { value: FlagLifecycle; label: string }[] = [
    { value: 'dev', label: 'Dev — not ready for anyone' },
    { value: 'beta', label: 'Beta — selected museums' },
    { value: 'ga', label: 'GA — generally available' },
    { value: 'deprecated', label: 'Deprecated — stop using' },
];

interface Draft {
    key: string;
    name: string;
    description: string;
    category: string;
    owner: string;
    lifecycle: FlagLifecycle;
    reason: string;
}

const EMPTY_DRAFT: Draft = {
    key: '',
    name: '',
    description: '',
    category: '',
    owner: '',
    lifecycle: 'dev',
    reason: '',
};

export function CreateFlagDialog({
    open,
    onClose,
    onCreated,
}: {
    open: boolean;
    onClose: () => void;
    onCreated: (key: string) => void;
}) {
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [confirming, setConfirming] = useState(false);
    const create = useCreateFlag();

    const set = <K extends keyof Draft>(field: K, value: Draft[K]) => {
        setDraft((current) => ({ ...current, [field]: value }));
    };

    const close = () => {
        setDraft(EMPTY_DRAFT);
        setConfirming(false);
        create.reset();
        onClose();
    };

    const rejection = rejectFlagKey(draft.key);
    // An untouched field is not yet a mistake — showing "a key is required" on
    // an empty form scolds the operator for not having typed anything yet.
    const keyError =
        draft.key.length > 0 && rejection ? explainFlagKeyRejection(rejection) : undefined;

    const complete =
        rejection === null &&
        draft.name.trim().length > 0 &&
        draft.description.trim().length > 0 &&
        draft.category.trim().length > 0 &&
        draft.owner.trim().length > 0;

    const submit = () => {
        create.mutate(
            {
                key: draft.key.trim(),
                name: draft.name.trim(),
                description: draft.description.trim(),
                category: draft.category.trim(),
                owner: draft.owner.trim(),
                lifecycle: draft.lifecycle,
                ...(draft.reason.trim().length > 0 ? { reason: draft.reason.trim() } : {}),
            },
            {
                onSuccess: (definition) => {
                    setDraft(EMPTY_DRAFT);
                    setConfirming(false);
                    onCreated(definition.key);
                },
                // A taken key is the one failure the operator fixes by editing
                // the field, so send them back to the form rather than leaving
                // them on a confirm screen whose only button re-fails.
                onError: (error) => {
                    if (error instanceof ApiError && error.isAlreadyExists) setConfirming(false);
                },
            },
        );
    };

    return (
        <Modal
            open={open}
            onClose={close}
            dismissable={!create.isPending}
            title={confirming ? 'Create this flag?' : 'New feature flag'}
            description={
                confirming
                    ? 'Keys cannot be renamed later — defaults, overrides and audit entries are all filed under them. Check it reads exactly as you intend.'
                    : 'The flag is created switched off, so this cannot change any behaviour on its own.'
            }
            footer={
                confirming ? (
                    <>
                        <Button
                            variant="ghost"
                            onClick={() => {
                                setConfirming(false);
                            }}
                            disabled={create.isPending}
                        >
                            Back
                        </Button>
                        <Button onClick={submit} disabled={create.isPending}>
                            {create.isPending ? 'Creating…' : 'Create flag'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button variant="ghost" onClick={close}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => {
                                setConfirming(true);
                            }}
                            disabled={!complete}
                        >
                            Review
                        </Button>
                    </>
                )
            }
        >
            {confirming ? (
                <div className="flex flex-col gap-4">
                    <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3.5">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-brand-700">
                            Create flag
                        </div>
                        <div className="mt-1.5 break-all font-mono text-[15px] font-semibold text-gray-900">
                            {draft.key.trim()}
                        </div>
                    </div>

                    <dl className="text-[13px]">
                        <Row label="Name">{draft.name.trim()}</Row>
                        <Row label="Category">{draft.category.trim()}</Row>
                        <Row label="Owner">{draft.owner.trim()}</Row>
                        <Row label="Lifecycle">{draft.lifecycle}</Row>
                        <Row label="Initial state">Off, no rollout gate</Row>
                    </dl>

                    {create.error && <WriteError error={create.error} />}

                    <p className="rounded-lg bg-gray-50 px-3.5 py-2.5 text-[12px] leading-relaxed text-gray-600">
                        Creating a flag here does not make it gate anything yet — VeevartBackend
                        resolves it <strong className="font-semibold">off</strong> until its read
                        path reads this catalog. Referencing the key in code is a separate step.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-4">
                    <TextField
                        label="Key"
                        value={draft.key}
                        onChange={(value) => {
                            set('key', value);
                        }}
                        placeholder="checkout.new-flow"
                        mono
                        autoFocus
                        error={keyError}
                        hint="Lowercase, dot-separated. This is permanent — it cannot be renamed."
                    />
                    <TextField
                        label="Name"
                        value={draft.name}
                        onChange={(value) => {
                            set('name', value);
                        }}
                        placeholder="New checkout flow"
                    />
                    <TextAreaField
                        label="What it gates"
                        value={draft.description}
                        onChange={(value) => {
                            set('description', value);
                        }}
                        placeholder="What breaks if this is turned off?"
                        hint="This row is the only place a flag's purpose is recorded now that no code catalog holds one."
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <TextField
                            label="Category"
                            value={draft.category}
                            onChange={(value) => {
                                set('category', value);
                            }}
                            placeholder="Payments"
                        />
                        <TextField
                            label="Owning team"
                            value={draft.owner}
                            onChange={(value) => {
                                set('owner', value);
                            }}
                            placeholder="Payments"
                        />
                    </div>
                    <SelectField
                        label="Lifecycle"
                        value={draft.lifecycle}
                        onChange={(value) => {
                            set('lifecycle', value);
                        }}
                        options={LIFECYCLE_OPTIONS}
                        hint="A label for operators. The switch that actually stops a flag being served is Archived, on the default."
                    />
                    <TextAreaField
                        label="Reason (optional)"
                        value={draft.reason}
                        onChange={(value) => {
                            set('reason', value);
                        }}
                        rows={2}
                        placeholder="Why this flag is being created"
                        hint="Recorded on the audit entry."
                    />

                    {create.error && <WriteError error={create.error} />}

                    <PropagationNotice />
                </div>
            )}
        </Modal>
    );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
            <dt className="shrink-0 text-gray-500">{label}</dt>
            <dd className="text-right font-medium text-gray-900">{children}</dd>
        </div>
    );
}
